import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Seo } from '@/components/Seo';
import { useLang } from '@/i18n/LanguageContext';
import { ui } from '@/i18n/content/ui';
import { pathFor } from '@/i18n/routes';
import { useArtwork, formatPrice, artworksDb } from '@/hooks/useArtworks';

const inquirySchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  message: z.string().trim().max(2000).optional(),
});

export default function ArtworkDetail() {
  const { slug } = useParams<{ slug: string }>();
  const lang = useLang();
  const labels = ui[lang];
  const { artwork, loading } = useArtwork(slug);

  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [fieldError, setFieldError] = useState<string | null>(null);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-6 py-24 text-sitefg/40">{labels.loading}</div>;
  }

  if (!artwork) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-24">
        <p className="text-sitefg/60">{labels.notFound}</p>
        <Link to={pathFor('nieuwwerk', lang)} className="mt-4 inline-block text-[var(--viz-accent)]">
          {labels.backToOverview}
        </Link>
      </div>
    );
  }

  const title = lang === 'nl' ? artwork.title_nl : artwork.title_en;
  const description = lang === 'nl' ? artwork.description_nl : artwork.description_en;
  const materials = lang === 'nl' ? artwork.materials_nl : artwork.materials_en;
  const price = formatPrice(artwork, lang);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = inquirySchema.safeParse(form);
    if (!parsed.success) {
      setFieldError(Object.values(parsed.error.flatten().fieldErrors).flat()[0] ?? 'Invalid input');
      return;
    }
    setFieldError(null);
    setStatus('sending');
    const { error } = await artworksDb.from('artwork_inquiries').insert({
      artwork_id: artwork.id,
      artwork_title: title,
      name: parsed.data.name,
      email: parsed.data.email,
      message: parsed.data.message ?? null,
      lang,
    });
    if (error) setStatus('error');
    else {
      setStatus('sent');
      setForm({ name: '', email: '', message: '' });
    }
  };

  return (
    <>
      <Seo
        title={`${title} · Perpetual Plexus`}
        description={(description ?? '').slice(0, 155) || `${title} — Rot8ion`}
        path={pathFor('nieuwwerk', lang, artwork.slug)}
      />
      <div className="max-w-5xl mx-auto px-6 pt-10 pb-24">
        <Link
          to={pathFor('nieuwwerk', lang)}
          className="text-sm text-sitefg/50 hover:text-[var(--viz-accent)] transition-colors"
        >
          {labels.backToOverview}
        </Link>

        <div className="mt-8 grid md:grid-cols-2 gap-10">
          <div className="space-y-4">
            {artwork.images.map((img, i) => (
              <img
                key={i}
                src={img.url}
                alt={img.alt || title}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="w-full aspect-square object-cover rounded-sm border border-sitefg/5"
              />
            ))}
          </div>

          <div>
            <h1 className="text-3xl md:text-4xl font-light tracking-tight">{title}</h1>
            {artwork.year && (
              <div className="mt-2 text-sm font-mono tracking-wider text-sitefg/40">
                {labels.year}: {artwork.year}
              </div>
            )}

            {description && (
              <p className="mt-6 leading-relaxed text-sitefg/75 whitespace-pre-line">{description}</p>
            )}

            <dl className="mt-8 space-y-2 text-sm">
              {materials && (
                <div className="flex gap-3">
                  <dt className="w-32 text-sitefg/40">{labels.materials}</dt>
                  <dd className="text-sitefg/75">{materials}</dd>
                </div>
              )}
              {artwork.dimensions && (
                <div className="flex gap-3">
                  <dt className="w-32 text-sitefg/40">{labels.dimensions}</dt>
                  <dd className="text-sitefg/75">{artwork.dimensions}</dd>
                </div>
              )}
            </dl>

            <div className="mt-8 text-lg text-[var(--viz-accent)]">
              {artwork.sold ? labels.soldOut : price ?? labels.onRequest}
            </div>

            <section className="mt-10 border-t border-sitefg/10 pt-8">
              <h2 className="text-xl font-light tracking-tight">{labels.interested}</h2>
              <p className="mt-2 text-sm text-sitefg/55">{labels.inquiryIntro}</p>

              {status === 'sent' ? (
                <p className="mt-6 text-sm text-[var(--viz-accent)]">{labels.sent}</p>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-widest text-sitefg/40 mb-1">
                      {labels.name}
                    </label>
                    <input
                      required
                      maxLength={100}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full border border-sitefg/15 rounded-sm px-3 py-2 bg-sitefg/5 focus:outline-none focus:border-[var(--viz-accent)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-widest text-sitefg/40 mb-1">
                      {labels.email}
                    </label>
                    <input
                      required
                      type="email"
                      maxLength={255}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full border border-sitefg/15 rounded-sm px-3 py-2 bg-sitefg/5 focus:outline-none focus:border-[var(--viz-accent)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-widest text-sitefg/40 mb-1">
                      {labels.message}
                    </label>
                    <textarea
                      rows={4}
                      maxLength={2000}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full border border-sitefg/15 rounded-sm px-3 py-2 bg-sitefg/5 focus:outline-none focus:border-[var(--viz-accent)]"
                    />
                  </div>

                  {fieldError && <p className="text-sm text-red-600">{fieldError}</p>}
                  {status === 'error' && <p className="text-sm text-red-600">{labels.sendFailed}</p>}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="px-5 py-2 rounded-sm bg-[var(--viz-accent)] text-white text-sm tracking-wide disabled:opacity-60"
                  >
                    {status === 'sending' ? labels.sending : labels.send}
                  </button>
                </form>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
